import _ from 'lodash';
import { mapGetters } from 'vuex';

import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import momentz from 'moment-timezone';
import moment from './bookedLoadMixin';
import { loadedMiles } from '../types/models/load';
import * as Timestamps from '@/types/timestamp';
import { createHelpers } from 'vuex-map-fields';

const { mapFields } = createHelpers({
  getterType: 'spaceTime/getField',
  mutationType: 'spaceTime/updateField',
});

export default {
  mixins: [
    bookedLoadMixin,
    planningLoadGroupsMixin,
  ],
  computed: {
    ...mapGetters('bookedLoads', {
      bookedLoads: 'bookedLoads',
      storeBookedLoadsCompanyId: 'companyId',
      loadingLoads: 'loadingLoads',
      bookedLoadsTime: 'timestamp',
    }),
    ...mapGetters('plannedLoads', {
      plannedLoads: 'list',
      loadingPlannedLoads: 'loadingLoads',
      plannedLoadsTime: 'timestamp',
      storePlannedLoadsCompanyId: 'companyId',
    }),
    ...mapFields([
      'daysIntoFuture',
    ]),
    spaceTimeTicksPerDay() {
      return 6;
    },
    spaceTimeHoursPerTick() {
      return 24 / this.spaceTimeTicksPerDay;
    },
    spaceTimeSliderMaxValue() {
      return (this.daysIntoFuture * this.spaceTimeTicksPerDay) - 1;
    },

    spaceTimeDistanceGradient() {
      return {
        0: '#dd0800',
        1: '#E21700',
        2: '#E82501',
        3: '#ED3401',
        4: '#F24301',
        5: '#F85102',
        6: '#fd6002',
        7: '#FD7202',
        8: '#FE8301',
        9: '#FE9501',
        10: '#FEA701',
        11: '#FEB901',
        12: '#FFCA00',
        13: '#ffdc00',
        14: '#EDD908',
        15: '#DCD511',
        16: '#CAD219',
        17: '#B8CF21',
        18: '#A6CC29',
        19: '#95C832',
        20: '#83c53a',
        21: '#74C452',
        22: '#66C26A',
        23: '#57C182',
        24: '#49BF9A',
        25: '#3ABEB2',
        26: '#2CBCCA',
        27: '#1dbbe2',
        28: '#2AB3D3',
        29: '#38ACC5',
        30: '#45A4B6',
        31: '#529CA7',
        32: '#5F9498',
        34: '#6D8D8A',
        33: '#7a857b',
        35: '#899189',
        36: '#989E98',
        37: '#A6AAA6',
        38: '#B5B7B5',
        39: '#c4c3c3',
      };
    },
  },
  methods: {
    isBookedLoadVisibleFromTimeMachine(bookedLoad) {
      return this.isBookedLoadPickedUpAfter(bookedLoad, this.currentFutureTime);
    },
    isPlannedLoadVisibleFromTimeMachine(plannedLoad) {
      return this.isPlannedLoadPickedUpAfter(plannedLoad, this.currentFutureTime);
    },
    bookedLoadPickupMoment(load) {
      const pickupLocation = _.get(load, 'tacticalInfo.loadDetails.pickupLocation'); // not sure if this is both V1 and V2
      const pickupTz = pickupLocation.googlePlace.timezone;
      const projectedPickup = load.pickupTime || load.tacticalInfo.estimatedPickup || load.tacticalInfo.scheduledPickupTime;

      return momentz(projectedPickup, moment.ISO_8601).tz(pickupTz);
    },
    hoursBetweenBookedLoadPickupAnd(load, since) {
      const pickup = this.bookedLoadPickupMoment(load);
      return Timestamps.hoursBetweenMoments(since, pickup);
    },
    isBookedLoadPickedUpAfter(load, at) {
      const hoursUntil = this.hoursBetweenBookedLoadPickupAnd(load, at);
      return hoursUntil >= 0;
    },
    bookedLoadProjectedDeliveryMoment(load) {
      const totalHours = loadedMiles(load) / 55; // 55mph

      const pickup = this.bookedLoadPickupMoment(load);
      return pickup.add(totalHours, 'hours');
    },
    plannedLoadPickupMoment(load) {
      const location = this.loadGroupShippingLocation(load);
      const tz = location.googlePlace.timezone;
      const projectedPickup = load.pickupTime || load.tacticalInfo.estimatedPickup || load.tacticalInfo.scheduledPickupTime;

      return momentz(projectedPickup, moment.ISO_8601).tz(tz);
    },
    hoursBetweenPlannedLoadPickupAnd(load, since) { // this is a V2 object
      const pickup = this.plannedLoadPickupMoment(load);
      return Timestamps.hoursBetweenMoments(since, pickup);
    },
    isPlannedLoadPickedUpAfter(load, at) {
      const hoursUntil = this.hoursBetweenPlannedLoadPickupAnd(load, at);
      return hoursUntil >= 0;
    },
    plannedLoadProjectedDeliveryMoment(load) {
      const totalHours = loadedMiles(load) / 55; // 55mph

      const pickup = this.plannedLoadPickupMoment(load);
      return pickup.add(totalHours, 'hours');
    },
    spaceTimeBookedLoadGradient(load) {
      const hoursUntilActualPickup = this.hoursBetweenBookedLoadPickupAnd(load, this.startingTime);
      const spaceTimeHours = Math.max(0, hoursUntilActualPickup);
      const ticks = spaceTimeHours / this.spaceTimeTicksPerDay;
      const t = Math.floor(ticks);
      const spaceTimeTicks = Math.min(t, this.spaceTimeSliderMaxValue);
      const ratio = spaceTimeTicks / this.spaceTimeSliderMaxValue;
      const idx = Math.floor(ratio * _.size(this.spaceTimeDistanceGradient));

      return this.spaceTimeDistanceGradient[idx];
    },
    spaceTimePlannedLoadGradient(load) {
      const hoursUntilActualPickup = this.hoursBetweenPlannedLoadPickupAnd(load, this.startingTime);
      const spaceTimeHours = Math.max(0, hoursUntilActualPickup);
      const ticks = spaceTimeHours / this.spaceTimeTicksPerDay;
      const t = Math.floor(ticks);
      const spaceTimeTicks = Math.min(t, (this.spaceTimeSliderMaxValue));
      const ratio = spaceTimeTicks / this.spaceTimeSliderMaxValue;
      const idx = Math.floor(ratio * _.size(this.spaceTimeDistanceGradient));

      return this.spaceTimeDistanceGradient[idx];
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/spaceTimeContinuumMixin.js